'use client';

import { Fragment, useState, type ReactNode } from 'react';
import { BLOCKS, INLINES, type Block, type Inline } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Text, { TEXT_TAG } from '@pixum/text';
import { Dialog } from '../dialog';
import { RichTextBody, isRichTextEmpty, renderHyperLink, renderTextOption } from '../richtText';
import { parseOpenDialogMarkers } from './parseOpenDialogMarkers';
import type { RichTextWithDialogsProps } from './types';
import styles from './RichTextWithDialogs.module.css';

// The sentence already sits inside a text element, so a paragraph adds no <p>; its trailing space splits paragraphs.
function renderInlineParagraph(_node: Block | Inline, children: ReactNode): ReactNode {
  return <>{children} </>;
}

/**
 * Renders a sentence whose `{{ openDialog(id:<dialogId>): <label> }}` markers become buttons opening the
 * rich text of `dialogs[dialogId]` in a dialog. The sentence is plain text or a Contentful rich text, whose
 * hyperlinks and marks are kept. Remaining props go to the wrapping `Text`.
 */
export function RichTextWithDialogs({
  sentence,
  dialogs,
  onDialogOpen,
  dialogRole,
  dialogFullScreen,
  closeLabel,
  tag = TEXT_TAG.P,
  ...textProps
}: RichTextWithDialogsProps) {
  const [openDialogId, setOpenDialogId] = useState<string | null>(null);

  if (!sentence || (typeof sentence !== 'string' && isRichTextEmpty(sentence))) {
    return null;
  }

  const openSection = openDialogId ? dialogs[openDialogId] : undefined;

  const openDialog = (dialogId: string) => {
    onDialogOpen?.(dialogId);
    setOpenDialogId(dialogId);
  };

  const renderMarkers = (text: string): ReactNode[] =>
    parseOpenDialogMarkers(text).map(({ text: segmentText, dialogId }, index) =>
      dialogId && dialogs[dialogId] ? (
        <button key={index} type="button" className={styles['dialogLink']} onClick={() => openDialog(dialogId)}>
          {segmentText}
        </button>
      ) : (
        <Fragment key={index}>{renderTextOption(segmentText)}</Fragment>
      )
    );

  return (
    <>
      <Text tag={tag} {...textProps}>
        {typeof sentence === 'string'
          ? renderMarkers(sentence)
          : documentToReactComponents(sentence, {
              renderNode: { [BLOCKS.PARAGRAPH]: renderInlineParagraph, [INLINES.HYPERLINK]: renderHyperLink },
              renderText: renderMarkers,
              stripEmptyTrailingParagraph: true
            })}
      </Text>
      {openSection && (
        <Dialog
          isOpen
          headline={openSection.title}
          role={dialogRole}
          fullScreen={dialogFullScreen}
          closeLabel={closeLabel}
          onClose={() => setOpenDialogId(null)}
        >
          {!isRichTextEmpty(openSection.richText) && <RichTextBody text={openSection.richText} />}
        </Dialog>
      )}
    </>
  );
}
